import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Pencil, X, MapPin, CalendarDays } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["ag", "conference", "atelier", "rencontre", "reunion", "partenaire"];
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("ghp-admin-token")}` });
const inputCls = "w-full border border-graphite/20 bg-white px-4 py-3 text-sm text-graphite focus:outline-none focus:border-forest transition-colors duration-200";
const labelCls = "overline-mono mb-2 block text-graphite-soft";
const EMPTY = { title_fr: "", title_en: "", date: "", end_date: "", location: "", type: "rencontre", description_fr: "", description_en: "", link: "" };

const EventsTab = () => {
  const { t, lang } = useLanguage();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events`);
      setEvents(data);
    } catch {
      toast.error(t("forms.error"));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/events/${editingId}`, form, { headers: authHeader() });
        toast.success(t("admin.eventUpdated"));
      } else {
        await axios.post(`${API}/admin/events`, form, { headers: authHeader() });
        toast.success(t("admin.eventSaved"));
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch {
      toast.error(t("forms.error"));
    }
    setSaving(false);
  };

  const startEdit = (ev) => {
    setForm({
      title_fr: ev.title_fr, title_en: ev.title_en || "", date: ev.date || "", end_date: ev.end_date || "",
      location: ev.location || "", type: ev.type, description_fr: ev.description_fr || "",
      description_en: ev.description_en || "", link: ev.link || "",
    });
    setEditingId(ev.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, { headers: authHeader() });
      toast.success(t("admin.deleted"));
      load();
    } catch {
      toast.error(t("forms.error"));
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-5" data-testid="admin-events-tab">
      <form onSubmit={submit} className="space-y-5 border border-graphite/10 bg-white p-6 lg:col-span-2" data-testid="admin-event-form">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold tracking-tight text-graphite">
            {editingId ? t("admin.eventEdit") : t("admin.eventNew")}
          </h2>
          {editingId && (
            <button type="button" onClick={() => { setForm(EMPTY); setEditingId(null); }} className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-graphite-soft hover:text-destructive transition-colors" data-testid="admin-event-cancel-edit">
              <X className="h-3.5 w-3.5" />
              {t("admin.cancel")}
            </button>
          )}
        </div>
        <div>
          <label className={labelCls}>{t("admin.titleFr")}</label>
          <input required className={inputCls} value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} data-testid="admin-event-title-fr" />
        </div>
        <div>
          <label className={labelCls}>{t("admin.titleEn")}</label>
          <input className={inputCls} value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="admin-event-title-en" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t("admin.date")}</label>
            <input required type="datetime-local" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="admin-event-date" />
          </div>
          <div>
            <label className={labelCls}>{t("admin.endDate")}</label>
            <input type="datetime-local" className={inputCls} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="admin-event-end-date" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t("admin.location")}</label>
            <input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="admin-event-location" />
          </div>
          <div>
            <label className={labelCls}>{t("admin.type")}</label>
            <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="admin-event-type">
              {EVENT_TYPES.map((ty) => (
                <option key={ty} value={ty}>{t(`events.typeLabels.${ty}`)}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>{t("admin.descFr")}</label>
          <textarea rows={3} className={inputCls} value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} data-testid="admin-event-desc-fr" />
        </div>
        <div>
          <label className={labelCls}>{t("admin.descEn")}</label>
          <textarea rows={3} className={inputCls} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} data-testid="admin-event-desc-en" />
        </div>
        <div>
          <label className={labelCls}>{t("admin.link")}</label>
          <input type="url" className={inputCls} value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="admin-event-link" />
        </div>
        <button type="submit" disabled={saving} className="w-full bg-forest px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-graphite disabled:opacity-50" data-testid="admin-event-submit">
          {saving ? t("forms.sending") : editingId ? t("admin.update") : t("admin.publish")}
        </button>
      </form>
      <div className="space-y-4 lg:col-span-3">
        {events.length === 0 && (
          <p className="border border-dashed border-graphite/20 py-16 text-center text-sm text-graphite-soft">{t("admin.empty")}</p>
        )}
        {events.map((ev) => (
          <article key={ev.id} className="flex items-start justify-between gap-4 border border-graphite/10 bg-white p-6" data-testid={`admin-event-${ev.id}`}>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-forest">{t(`events.typeLabels.${ev.type}`)}</p>
              <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-graphite">{ev.title_fr}</h3>
              <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-graphite-soft">
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(ev.date)}</span>
                {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{ev.location}</span>}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button onClick={() => startEdit(ev)} aria-label={t("admin.eventEdit")} className="text-graphite/30 transition-colors hover:text-forest" data-testid={`admin-event-edit-${ev.id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => del(ev.id)} aria-label={t("admin.delete")} className="text-graphite/30 transition-colors hover:text-destructive" data-testid={`admin-event-delete-${ev.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default EventsTab;
